import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';

import Menu from '../Menu/Menu';
import MenuLabel from '../MenuLabel/MenuLabel';
import MenuContent from '../MenuContent/MenuContent';
import MenuItem from '../MenuItem/MenuItem';
import ExternalLink from '../ExternalLink/ExternalLink';
import { InlineTextButton } from '../Button/Button';
import IconSocialMediaFacebook from '../IconSocialMediaFacebook/IconSocialMediaFacebook';
import IconSocialMediaTwitter from '../IconSocialMediaTwitter/IconSocialMediaTwitter';

import css from './ShareButton.module.css';

const COPIED_TIMEOUT_MS = 2000;

// Simple, self-contained share icon so we don't depend on a shared asset.
const IconShare = () => (
  <svg
    className={css.shareIcon}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

/**
 * A small "Share" control that opens a dropdown with copy-link, native share
 * (when supported) and social sharing options.
 *
 * All sharing uses plain URL-intent links or the browser's own share sheet, so
 * no third-party SDKs, pixels or trackers are loaded. Only the public URL that
 * is passed in is ever shared.
 *
 * @component
 * @param {Object} props
 * @param {string} props.url - The absolute, public URL to share (required).
 * @param {string?} props.title - Optional text used as the share message.
 * @param {ReactNode?} props.label - Optional custom label for the button (defaults to "Share").
 * @param {string?} props.className - Extra style rules for the root element.
 * @param {string?} props.rootClassName - Overwrite the component's own css.root.
 * @returns {JSX.Element|null} Share dropdown, or null when no url is given.
 */
const ShareButton = props => {
  const { className, rootClassName, url, title, label } = props;
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    // Clear any pending "Copied!" reset timer if the component unmounts.
    return () => clearTimeout(copiedTimeoutRef.current);
  }, []);

  if (!url) {
    return null;
  }

  const shareText = title || '';
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);
  const whatsappText = encodeURIComponent(shareText ? `${shareText} ${url}` : url);

  const whatsappHref = `https://wa.me/?text=${whatsappText}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const twitterHref = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;

  // The clipboard and Web Share APIs only exist in the browser (and mostly in
  // secure contexts). Guard on `mounted` so the server render and the first
  // client render match, avoiding hydration mismatches.
  const canNativeShare =
    mounted && typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canCopy =
    mounted &&
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard &&
    !!navigator.clipboard.writeText;

  const handleNativeShare = () => {
    // Pass the message as 'text', not 'title': share targets such as WhatsApp
    // compose the message from 'text' + 'url' and ignore 'title' entirely, so
    // sharing via 'title' would silently drop our message.
    // The promise rejects if the user dismisses the share sheet – ignore that.
    navigator.share({ text: shareText || undefined, url }).catch(() => {});
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_TIMEOUT_MS);
      })
      .catch(() => {});
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    // preferScreenWidthOnMobile: the button sits at the left edge of the
    // content on mobile, so a dropdown right-aligned to it would run off the
    // left of the screen. On mobile the menu takes the screen width instead.
    <Menu className={classes} contentPosition="left" preferScreenWidthOnMobile>
      <MenuLabel className={css.menuLabel} isOpenClassName={css.menuIsOpen}>
        <IconShare />
        <span className={css.menuLabelText}>
          {label || <FormattedMessage id="ShareButton.label" />}
        </span>
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        {canNativeShare ? (
          <MenuItem key="native">
            <InlineTextButton className={css.menuItem} onClick={handleNativeShare}>
              <FormattedMessage id="ShareButton.nativeShare" />
            </InlineTextButton>
          </MenuItem>
        ) : null}
        {canCopy ? (
          <MenuItem key="copy">
            <InlineTextButton className={css.menuItem} onClick={handleCopy}>
              {copied ? (
                <FormattedMessage id="ShareButton.copied" />
              ) : (
                <FormattedMessage id="ShareButton.copyLink" />
              )}
            </InlineTextButton>
          </MenuItem>
        ) : null}
        <MenuItem key="whatsapp">
          <ExternalLink href={whatsappHref} className={css.menuItem}>
            <FormattedMessage id="ShareButton.whatsapp" />
          </ExternalLink>
        </MenuItem>
        <MenuItem key="facebook">
          <ExternalLink href={facebookHref} className={css.menuItem}>
            <IconSocialMediaFacebook className={css.socialIcon} />
            <FormattedMessage id="ShareButton.facebook" />
          </ExternalLink>
        </MenuItem>
        <MenuItem key="twitter">
          <ExternalLink href={twitterHref} className={css.menuItem}>
            <IconSocialMediaTwitter className={css.socialIcon} />
            <FormattedMessage id="ShareButton.twitter" />
          </ExternalLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default ShareButton;
